// =============================================================================
// NewDeliveryNotePage — create a draft from an order
// =============================================================================
// Pre-fills lines from the order. User picks a driver + vehicle + scheduled date.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { PageScaffold } from '../../../components/PageScaffold';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';

interface OrderDetail {
  id: string;
  number: string;
  shopId: string;
  fulfillmentType: string;
  deliveryAddress: string | null;
  deliveryAddressPrecise: string | null;
  deliveryDate: string | null;
  status: string;
  customer: { name: string; companyName: string | null };
  lines: Array<{ id: string; qty: number; product: { id: string; sku: string; name: string } }>;
}

interface DriverOption {
  id: string;
  name: string;
  role: string;
  homeShopId: string | null;
}

export function NewDeliveryNotePage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderId = params.get('orderId') ?? '';
  const [driverId, setDriverId] = useState('');
  const [vehicleRef, setVehicleRef] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [lineQtys, setLineQtys] = useState<Record<string, number>>({});
  const [err, setErr] = useState<string | null>(null);

  const order = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => apiGet<OrderDetail>(`/orders/${orderId}`),
    enabled: !!orderId,
  });

  const drivers = useQuery({
    queryKey: ['users-for-driver'],
    queryFn: () => apiGet<DriverOption[]>('/users'),
  });

  useEffect(() => {
    if (order.data) {
      setLineQtys(Object.fromEntries(order.data.lines.map((l) => [l.id, l.qty])));
      if (order.data.deliveryDate) setScheduledDate(order.data.deliveryDate.slice(0, 10));
    }
  }, [order.data]);

  const create = useMutation({
    mutationFn: () =>
      apiPost<{ id: string }>('/delivery-notes', {
        orderId,
        driverId: driverId || undefined,
        vehicleRef: vehicleRef || undefined,
        scheduledDate: scheduledDate || undefined,
        lines: Object.entries(lineQtys)
          .filter(([_id, qty]) => qty > 0)
          .map(([id, qty]) => ({ orderLineId: id, qtyDispatched: qty })),
      }),
    onSuccess: (dn) => navigate(`/delivery-notes/${dn.id}`),
    onError: (e) => setErr(getApiError(e).message),
  });

  if (!orderId)
    return (
      <PageScaffold title="New delivery note">
        <div className="text-ink-3">Need an orderId in the URL.</div>
      </PageScaffold>
    );
  if (order.isLoading)
    return (
      <PageScaffold title="New delivery note">
        <div className="text-ink-3">Loading…</div>
      </PageScaffold>
    );
  if (!order.data)
    return (
      <PageScaffold title="New delivery note">
        <div className="text-ink-3">Order not found.</div>
      </PageScaffold>
    );
  const o = order.data;
  const display = o.customer.companyName ?? o.customer.name;
  const driverOptions = (drivers.data ?? []).filter((u) =>
    ['operations', 'sales_girl', 'owner', 'admin'].includes(u.role),
  );

  return (
    <PageScaffold
      width="form"
      back={{ to: `/orders/${o.id}`, label: `Order ${o.number}` }}
      title="New delivery note"
      subtitle={
        <>
          For order <span className="kc-mono text-ink">{o.number}</span> · {display}
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => navigate(`/orders/${o.id}`)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              create.mutate();
            }}
            disabled={create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create delivery note'}
          </Button>
        </>
      }
    >
      <Card title="Delivery address">
        <div className="text-sm text-ink whitespace-pre-line">
          {o.deliveryAddressPrecise || o.deliveryAddress || '— no address on order —'}
        </div>
      </Card>

      <Card title="Driver + vehicle" subtitle="Optional at draft stage — fill before dispatch">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Driver</span>
            <select
              value={driverId}
              onChange={(e) => setDriverId(e.target.value)}
              className="kc-input"
            >
              <option value="">— unassigned —</option>
              {driverOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.role})
                </option>
              ))}
            </select>
          </label>
          <Input
            label="Vehicle ref"
            value={vehicleRef}
            onChange={(e) => setVehicleRef(e.target.value)}
            placeholder="e.g. TRUCK-001"
          />
          <Input
            label="Scheduled date"
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
          />
        </div>
      </Card>

      <Card title="Lines" subtitle="Set qty to 0 to skip a line" bodyClassName="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
              <th className="px-6 py-2 font-medium">Product</th>
              <th className="px-3 py-2 font-medium text-right w-24">Ordered</th>
              <th className="px-3 py-2 font-medium text-right w-32">To dispatch</th>
            </tr>
          </thead>
          <tbody>
            {o.lines.map((l) => (
              <tr key={l.id} className="border-b border-rule-2">
                <td className="px-6 py-3">
                  <div className="text-ink">{l.product.name}</div>
                  <div className="text-xs text-ink-3 kc-mono">{l.product.sku}</div>
                </td>
                <td className="px-3 py-3 text-right text-ink-3 kc-mono">{l.qty}</td>
                <td className="px-3 py-3 text-right">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    max={l.qty}
                    value={lineQtys[l.id] ?? 0}
                    onChange={(e) =>
                      setLineQtys((cur) => ({
                        ...cur,
                        [l.id]: Math.max(0, Math.min(l.qty, Number(e.target.value) || 0)),
                      }))
                    }
                    className="kc-input text-sm text-right w-24"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {err && <div className="text-sm text-negative">{err}</div>}
    </PageScaffold>
  );
}
